import { ReactNode, createContext, useContext, useState } from 'react';
import { ClarityEvents } from '../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../lib/clarity/utils/captureEvent.utils';
import GlobalSearchModal from './GlobalSearchModal';
import useGlobalSearchShortcut from './hooks/useGlobalSearchShortcut';

interface GlobalSearchContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | undefined>(undefined);

interface GlobalSearchProviderProps {
  children: ReactNode;
}

function GlobalSearchProvider({ children }: GlobalSearchProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const openFromShortcut = () => {
    captureEvent(ClarityEvents.GLOBAL_SEARCH_OPENED_BY_SHORTCUT);
    open();
  };

  useGlobalSearchShortcut(openFromShortcut);

  return (
    <GlobalSearchContext.Provider value={{ isOpen, open, close }}>
      {children}
      <GlobalSearchModal isOpen={isOpen} onClose={close} />
    </GlobalSearchContext.Provider>
  );
}

export const useGlobalSearchContext = (): GlobalSearchContextValue => {
  const context = useContext(GlobalSearchContext);

  if (!context) {
    throw new Error('useGlobalSearchContext must be used within a GlobalSearchProvider');
  }

  return context;
};

export default GlobalSearchProvider;
